/**
 * CollapsibleBox — a titled SR-sidebar section that collapses to just its
 * header. Reuses the editor's configurable box style; collapse state is
 * persisted per section via the shared panel-layout store.
 *
 * Synth VR only: a box released outside its sidebar floats over the editor, is
 * moved by its title with magnetic edges, and docks back from an icon (SRVR-0358).
 */

import React, { useEffect, useReducer, useRef } from 'react';
import ReactDOM from 'react-dom';
import { usePanelLayout, useSidebarOrder } from '../ui/panelLayoutStore';
import { useFloatingPanels, clampToViewport, snapRect, type Rect } from '../ui/floatingPanelStore';
import { useEditorStore } from '../../store/editorStore';
import { color, font } from '../ui/theme';
import { IcTriRight, IcTriDown, IcPanels } from '../ui/icons';

const PANEL_DND_MIME = 'text/sr-panel';

/** Rects of every other sidebar box on screen, docked or floating — what a moving box snaps to. */
function otherPanelRects(id: string): Rect[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-sr-panel]'))
    .filter(el => el.dataset.srPanel !== id)
    .map(el => el.getBoundingClientRect());
}

/**
 * The one panel gesture in flight — a pull out of a sidebar, or a floating box's move — as the function
 * that ends it. Each gesture listens on the window and is ended by an event the browser does not always
 * deliver there (`dragend` goes to a title that floating has just removed from the page; `pointerup`
 * never comes after a cancel or a release outside the window), and a listener left behind moved its box
 * along with the next one dragged. Starting a gesture ends the last, so only one box ever follows.
 */
let endActiveGesture: (() => void) | null = null;
function beginGesture(end: () => void): void {
  endActiveGesture?.();
  endActiveGesture = end;
}

/** Clamp a drop point into the window, then let the magnet pull it onto a neighbour's edge. */
export function placeFloating(id: string, x: number, y: number, w: number, h: number): { x: number; y: number } {
  const c = clampToViewport(x, y, w, window.innerWidth, window.innerHeight);
  const r = snapRect({ left: c.x, top: c.y, right: c.x + w, bottom: c.y + h }, otherPanelRects(id), window.innerWidth, window.innerHeight);
  return { x: r.left, y: r.top };
}

export default function CollapsibleBox({
  id, title, boxStyle, defaultSidebar = 'right', headerAction, placeAfter, children,
}: {
  /** A panel this one is first placed directly after, in a layout that already exists: it takes that
   *  panel's order, and as it renders after it, the tie keeps it there (the Tools box, SRVR-0351 AC4). */
  placeAfter?: string;
  /** Stable id for persistence (usually the title). */
  id: string;
  title: string;
  /** The editor's configurable container style (interfaceSettings-driven). */
  boxStyle: React.CSSProperties;
  defaultSidebar?: 'left' | 'right';
  /** Optional control pinned to the RIGHT of the header (e.g. the Minimap's ⚙
   *  options button) — same slot as the Flow editor's Group. Absolutely
   *  positioned so the title stays centred, and its clicks never collapse. */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  const collapsed = usePanelLayout(s => s.layouts.sr?.[id]?.collapsed ?? false);
  const order = usePanelLayout(s => s.layouts.sr?.[id]?.order ?? 0);
  const toggleCollapse = usePanelLayout(s => s.toggleCollapse);
  const register = usePanelLayout(s => s.register);
  const movePanel = usePanelLayout(s => s.movePanel);
  const sidebarIds = useSidebarOrder('sr', defaultSidebar);
  const [dragOver, setDragOver] = React.useState(false);
  // Floating is Synth VR's alone; Console shares this box and the `sr` layout and keeps docking only.
  const isVr = useEditorStore(s => s.activeView === 'synth-rider');
  const floatPos = useFloatingPanels(s => (isVr ? s.panels[id] : undefined));
  const boxRef = useRef<HTMLDivElement | null>(null);
  /** Set while a floating box's title is dragged, so the click that ends the drag does not collapse it. */
  const movedRef = useRef(false);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const after = placeAfter ? usePanelLayout.getState().get('sr', placeAfter) : undefined;
    register('sr', id, defaultSidebar, after && after.sidebar === defaultSidebar ? after.order : 0);
  }, [id, defaultSidebar, register, placeAfter]);
  // A floating box re-clamps when the window shrinks, so it can always be reached and docked.
  useEffect(() => {
    if (!floatPos) return;
    window.addEventListener('resize', rerender);
    return () => window.removeEventListener('resize', rerender);
  }, [floatPos]);

  // Synth VR: a drop outside both sidebars floats the box there. The window accepts the drag only
  // while one of these boxes is in flight; a drop on another box is the reorder above and stops here.
  const startFloatDrag = (e: React.DragEvent) => {
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const grabX = e.clientX - r.left, grabY = e.clientY - r.top, w = r.width, h = r.height;
    const over = (ev: DragEvent) => { if (ev.dataTransfer?.types.includes(PANEL_DND_MIME)) ev.preventDefault(); };
    const drop = (ev: DragEvent) => {
      if (!ev.dataTransfer?.types.includes(PANEL_DND_MIME)) return;
      end();   // a drop ends the drag whatever it lands on
      ev.preventDefault();
      if (ev.dataTransfer.getData(PANEL_DND_MIME) !== id) return;   // another box's drag
      if ((ev.target as HTMLElement | null)?.closest?.('.overlay-scroll')) return;   // inside a sidebar
      const p = placeFloating(id, ev.clientX - grabX, ev.clientY - grabY, w, h);
      useFloatingPanels.getState().float(id, { x: p.x, y: p.y, w });
    };
    const end = () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
      window.removeEventListener('dragend', end);
      if (endActiveGesture === end) endActiveGesture = null;
    };
    beginGesture(end);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    window.addEventListener('dragend', end);
  };

  const headerActionSlot = headerAction && (
    <span
      style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      draggable={false}
      onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      {headerAction}
    </span>
  );

  if (floatPos) {
    const { x, y } = clampToViewport(floatPos.x, floatPos.y, floatPos.w, window.innerWidth, window.innerHeight);
    const onPointerDown = (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      const box = boxRef.current;
      if (!box) return;
      e.preventDefault();
      movedRef.current = false;
      const startX = e.clientX, startY = e.clientY;
      const h = box.getBoundingClientRect().height;
      // Captured, the release reaches us even outside the window; a cancel or a lost capture ends it too.
      const handle = e.currentTarget as HTMLElement;
      try { handle.setPointerCapture?.(e.pointerId); } catch { /* no pointer to capture (synthetic event) */ }
      const move = (ev: PointerEvent) => {
        if (!movedRef.current && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 3) return;
        movedRef.current = true;
        const p = placeFloating(id, x + ev.clientX - startX, y + ev.clientY - startY, floatPos.w, h);
        useFloatingPanels.getState().move(id, p.x, p.y);
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        handle.removeEventListener('lostpointercapture', up);
        if (endActiveGesture === up) endActiveGesture = null;
        if (movedRef.current) useFloatingPanels.getState().save();
      };
      beginGesture(up);
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
      handle.addEventListener('lostpointercapture', up);
    };
    return ReactDOM.createPortal(
      <div
        ref={boxRef}
        data-sr-panel={id}
        data-sr-floating=""
        style={{
          ...boxStyle, ...floatingBoxStyle,
          left: x, top: y, width: floatPos.w, maxHeight: `calc(100vh - ${Math.max(0, y)}px)`,
        }}
      >
        <div
          onPointerDown={onPointerDown}
          onClick={() => { if (movedRef.current) { movedRef.current = false; return; } toggleCollapse('sr', id); }}
          title="Click to collapse · drag to move"
          style={{ ...headerStyle, position: 'relative', cursor: 'move', touchAction: 'none', marginBottom: collapsed ? 0 : 1 }}
        >
          <button
            type="button"
            aria-label={`Dock ${title} to the sidebar`}
            title="Return to the sidebar"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); useFloatingPanels.getState().dock(id); }}
            style={dockBtnStyle}
          >
            <IcPanels size={12} />
          </button>
          <span style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>{collapsed ? <IcTriRight size={11} /> : <IcTriDown size={11} />}</span>
          <span style={labelStyle}>{title}</span>
          {headerActionSlot}
        </div>
        {!collapsed && children}
      </div>,
      document.body,
    );
  }

  return (
    <div
      ref={boxRef}
      data-sr-panel={id}
      style={{ ...boxStyle, order, ...(dragOver ? { outline: '2px dashed oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.7)', outlineOffset: 2 } : null) }}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(PANEL_DND_MIME)) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        const dragged = e.dataTransfer.getData(PANEL_DND_MIME);
        if (!dragged || dragged === id) return;
        // Reorder within the same sidebar only.
        if (usePanelLayout.getState().get('sr', dragged)?.sidebar !== defaultSidebar) return;
        e.preventDefault(); e.stopPropagation();
        const idx = sidebarIds.indexOf(id);
        movePanel('sr', dragged, defaultSidebar, idx < 0 ? sidebarIds.length : idx);
      }}
    >
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(PANEL_DND_MIME, id); e.dataTransfer.effectAllowed = 'move';
          if (isVr) startFloatDrag(e);
        }}
        onClick={() => toggleCollapse('sr', id)}
        title={isVr ? 'Click to collapse · drag to reorder, or out of the sidebar to float' : 'Click to collapse · drag to reorder'}
        style={{ ...headerStyle, position: 'relative', cursor: 'grab', marginBottom: collapsed ? 0 : 1 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>{collapsed ? <IcTriRight size={11} /> : <IcTriDown size={11} />}</span>
        <span style={labelStyle}>{title}</span>
        {headerActionSlot}
      </div>
      {!collapsed && children}
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  userSelect: 'none',
  color: color.accentSoft,
};

const labelStyle: React.CSSProperties = {
  // Matches Flow's groupLabelStyle so section titles read identically.
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  fontFamily: font.sans,
};

// Above the viewport and sidebars, below dialogs (10000) and tooltips.
const floatingBoxStyle: React.CSSProperties = {
  position: 'fixed',
  zIndex: 50,
  boxSizing: 'border-box',
  overflowY: 'auto',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 24px oklch(0.05 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.5)',
};

const dockBtnStyle: React.CSSProperties = {
  position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 20, height: 20, padding: 0, borderRadius: 6, cursor: 'pointer',
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.55)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  color: color.accentSoft,
};
